import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, Disclaimer } from "@/components/Common";
import StatusBadge from "@/components/StatusBadge";
import { ShieldAlert, AlertTriangle, Gavel, Search, CheckCircle2, XCircle, ExternalLink, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { CLEARANCE_STATUSES } from "@/lib/constants";
import { useAuth, canChangeClearance } from "@/lib/auth";

// MP5 — Compliance Review enhanced:
//  • Track selector/search + filter views
//  • Side panel detail: summary card, required-docs checklist, missing-docs list,
//    risk flags list, suggested next action, attorney-review toggle, clearance
//    selector, human approval area, internal reviewer notes
//  • Always shows legal boundary language.
// No AI is invoked anywhere. Engine is fully deterministic.

const ATTENTION = "needs_attention";

export default function ComplianceReview() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(ATTENTION);
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);     // { track, evaluation, documents }
  const [detailLoading, setDetailLoading] = useState(false);
  const [reviewerNotes, setReviewerNotes] = useState("");
  const [savingClearance, setSavingClearance] = useState(false);

  const canAct = canChangeClearance(user);

  async function refreshList() {
    setLoading(true);
    try {
      const r = await api.get("/tracks");
      setTracks(r.data || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }

  useEffect(() => { refreshList(); }, []);

  async function openDetail(trackId) {
    setSelectedId(trackId);
    setDetailLoading(true);
    setDetail(null);
    setReviewerNotes("");
    try {
      const [tRes, cRes] = await Promise.all([
        api.get(`/tracks/${trackId}`),
        api.get(`/tracks/${trackId}/compliance`),
      ]);
      setDetail({
        track: tRes.data,
        evaluation: cRes.data.evaluation,
        documents: cRes.data.documents || [],
      });
    } catch (e) {
      toast.error(errMsg(e));
      setSelectedId(null);
    } finally {
      setDetailLoading(false);
    }
  }

  function closeDetail() {
    setSelectedId(null);
    setDetail(null);
    setReviewerNotes("");
  }

  async function setClearance(target) {
    if (!detail?.track) return;
    setSavingClearance(true);
    try {
      await api.post(`/tracks/${detail.track.id}/clearance`, {
        clearance_status: target,
        notes: reviewerNotes,
      });
      toast.success(`Clearance set to ${target}`);
      await openDetail(detail.track.id);
      await refreshList();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSavingClearance(false); }
  }

  async function toggleAttorneyReview(required) {
    if (!detail?.track) return;
    try {
      await api.post(`/tracks/${detail.track.id}/attorney-review`, {
        attorney_review_required: required,
        attorney_review_reason: required ? (reviewerNotes || "Flagged from Compliance Review") : "",
      });
      toast.success(required ? "Flagged for Attorney Review" : "Attorney Review cleared");
      await openDetail(detail.track.id);
      await refreshList();
    } catch (e) { toast.error(errMsg(e)); }
  }

  const filtered = useMemo(() => {
    let rows = tracks;
    if (filter === "needs_attention") {
      rows = rows.filter(t =>
        ["Incomplete", "Needs Review", "Attorney Review Required"].includes(t.clearance_status)
        || (t.missing_documents || []).length > 0
        || t.attorney_review_required,
      );
    } else if (filter === "attorney") {
      rows = rows.filter(t => t.clearance_status === "Attorney Review Required" || t.attorney_review_required);
    } else if (filter === "missing_docs") {
      rows = rows.filter(t => (t.missing_documents || []).length > 0);
    } else if (filter === "high_risk") {
      rows = rows.filter(t => ["High", "Critical"].includes(t.risk_level));
    } else if (filter === "ready") {
      rows = rows.filter(t => ["Client-Ready", "Corporate-Ready"].includes(t.clearance_status));
    }
    if (q) {
      const needle = q.toLowerCase();
      rows = rows.filter(t => [
        t.track_id, t.track_title, t.artist, t.isrc, t.catalog_code,
      ].filter(Boolean).join(" ").toLowerCase().includes(needle));
    }
    return rows;
  }, [tracks, filter, q]);

  return (
    <div data-testid="compliance-page">
      <PageHeader
        title="Compliance Review"
        subtitle="Rules-based deterministic review. Compliance support only · Not legal advice · Final clearance may require qualified music/IP counsel."
      />

      <Card className="mb-4">
        <CardContent className="p-4 flex flex-wrap gap-3 items-end">
          <div className="min-w-[260px]">
            <label className="text-xs text-muted-foreground">View</label>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="compliance-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="needs_attention">Needs attention</SelectItem>
                <SelectItem value="missing_docs">Missing documents</SelectItem>
                <SelectItem value="attorney">Attorney Review Required</SelectItem>
                <SelectItem value="high_risk">High / Critical risk</SelectItem>
                <SelectItem value="ready">Client / Corporate-Ready</SelectItem>
                <SelectItem value="all">All tracks</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[260px]">
            <label className="text-xs text-muted-foreground">Track selector</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Title, artist, ISRC, catalog code…"
                value={q}
                onChange={e => setQ(e.target.value)}
                className="pl-9"
                data-testid="compliance-search-input"
              />
            </div>
          </div>
          <span className="text-xs text-muted-foreground self-end pb-2" data-testid="compliance-result-count">
            {filtered.length} matching
          </span>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Track</TableHead>
                <TableHead>Risk</TableHead>
                <TableHead>Missing documents</TableHead>
                <TableHead>Flags</TableHead>
                <TableHead>Clearance</TableHead>
                <TableHead>Human approval</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Loading…</TableCell>
                </TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">Nothing in this view.</TableCell>
                </TableRow>
              )}
              {filtered.map(t => (
                <TableRow
                  key={t.id}
                  className="cursor-pointer hover:bg-secondary/40"
                  onClick={() => openDetail(t.id)}
                  data-testid={`compliance-row-${t.id}`}
                >
                  <TableCell>
                    <div className="font-medium">{t.track_title || "(untitled)"}</div>
                    <div className="text-xs text-muted-foreground">{t.artist || "—"} · <span className="font-mono">{t.track_id}</span></div>
                  </TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-1 rounded ${["High", "Critical"].includes(t.risk_level) ? "status-attorney" : t.risk_level === "Medium" ? "status-needs-review" : "status-incomplete"}`}>
                      {t.risk_level || "Low"}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">
                    {(t.missing_documents || []).length === 0 ? (
                      <span className="text-muted-foreground text-xs">None</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {(t.missing_documents || []).slice(0, 3).map((d, i) => (
                          <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary">{d}</span>
                        ))}
                        {(t.missing_documents || []).length > 3 && (
                          <span className="text-xs text-muted-foreground">+{t.missing_documents.length - 3}</span>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      {t.attorney_review_required && <span className="inline-flex items-center gap-1 text-[11px] text-[hsl(0_70%_42%)]"><Gavel className="w-3 h-3" />Attorney</span>}
                      {t.sample_or_interpolation_flag && <span className="inline-flex items-center gap-1 text-[11px] text-[hsl(28_80%_38%)]"><AlertTriangle className="w-3 h-3" />Sample</span>}
                      {t.ownership_dispute_flag && <span className="inline-flex items-center gap-1 text-[11px] text-[hsl(0_70%_42%)]"><ShieldAlert className="w-3 h-3" />Dispute</span>}
                      {t.estate_or_heirship_flag && <span className="inline-flex items-center gap-1 text-[11px] text-[hsl(0_70%_42%)]"><ShieldAlert className="w-3 h-3" />Estate</span>}
                      {t.third_party_approval_required && <span className="inline-flex items-center gap-1 text-[11px] text-[hsl(28_80%_38%)]"><AlertTriangle className="w-3 h-3" />3rd-party</span>}
                    </div>
                  </TableCell>
                  <TableCell><StatusBadge status={t.clearance_status} /></TableCell>
                  <TableCell>
                    {t.human_approved_by ? (
                      <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded status-client">
                        <CheckCircle2 className="w-3 h-3" />{(t.human_approval_date || "").slice(0, 10) || "—"}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded status-incomplete">
                        <XCircle className="w-3 h-3" />Pending
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Disclaimer>
        Compliance support only · Not legal advice · Final clearance may require qualified music/IP counsel.
      </Disclaimer>

      <Sheet open={!!selectedId} onOpenChange={(v) => { if (!v) closeDetail(); }}>
        <SheetContent side="right" className="!max-w-[640px] sm:!max-w-[640px] w-full overflow-y-auto" data-testid="compliance-detail-panel">
          {detailLoading || !detail ? (
            <div className="py-10 text-center text-muted-foreground">Loading…</div>
          ) : (
            <DetailPanel
              detail={detail}
              canAct={canAct}
              reviewerNotes={reviewerNotes}
              setReviewerNotes={setReviewerNotes}
              setClearance={setClearance}
              toggleAttorneyReview={toggleAttorneyReview}
              savingClearance={savingClearance}
              onOpenTrack={() => navigate(`/tracks/${detail.track.id}`)}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function DetailPanel({ detail, canAct, reviewerNotes, setReviewerNotes, setClearance, toggleAttorneyReview, savingClearance, onOpenTrack }) {
  const { track, evaluation, documents } = detail;
  const required = ["Split Sheet", "Master Ownership Confirmation", "Letter of Direction / LOD", "Admin Agreement", "LOC Certificate", "DACR Certificate"];
  const acceptedStates = new Set(["Received", "Under Review", "Accepted", "Archived Offline"]);
  const docIndex = {};
  for (const d of documents) docIndex[d.document_type] = d;

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2">
          <span data-testid="detail-track-title">{track.track_title || "(untitled)"}</span>
          <button type="button" onClick={onOpenTrack} className="ml-auto text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="detail-open-track">
            Open editor <ExternalLink className="w-3 h-3" />
          </button>
        </SheetTitle>
        <div className="text-xs text-muted-foreground">
          <span className="font-mono">{track.track_id}</span> · {track.artist || "—"} · {track.genre || "—"}
        </div>
      </SheetHeader>

      <div className="space-y-4 mt-4">
        {/* Summary */}
        <Card>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-3">
              <StatusBadge status={track.clearance_status} />
              <span className="text-xs text-muted-foreground">Risk: <span className="font-medium text-foreground">{track.risk_level || "Low"}</span></span>
              {track.attorney_review_required && (
                <span className="text-xs flex items-center gap-1 text-[hsl(0_70%_42%)]"><Gavel className="w-3.5 h-3.5" /> Attorney Review Required</span>
              )}
            </div>
            <div className="text-xs text-muted-foreground">Suggested next action</div>
            <div className="text-sm" data-testid="detail-next-action">{evaluation.next_action}</div>
          </CardContent>
        </Card>

        {/* Required documents checklist */}
        <Card>
          <CardContent className="p-4">
            <div className="font-display text-base mb-2">Required documents</div>
            <ul className="space-y-1.5">
              {required.map((dt) => {
                const rec = docIndex[dt];
                const ok = !!rec && acceptedStates.has(rec.document_status);
                return (
                  <li key={dt} className="flex items-center gap-2 text-sm" data-testid={`detail-doc-${dt}`}>
                    {ok ? <CheckCircle2 className="w-4 h-4 text-[hsl(140_40%_40%)]" /> : <XCircle className="w-4 h-4 text-[hsl(28_80%_38%)]" />}
                    <span>{dt}</span>
                    <span className="ml-auto text-xs text-muted-foreground">{rec ? rec.document_status : "Missing"}</span>
                  </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>

        {/* Missing + risk flags */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <SectionList icon={ShieldAlert} title="Missing documents (baseline)" items={evaluation.missing_documents} tone="hsl(28 80% 38%)" testid="detail-missing-docs" />
            <SectionList icon={AlertTriangle} title="Required metadata still missing" items={evaluation.metadata_missing} tone="hsl(28 80% 38%)" testid="detail-metadata-missing" />
            <SectionList icon={Gavel} title="Risk flags" items={evaluation.risk_flags} tone="hsl(0 70% 42%)" testid="detail-risk-flags" />
          </CardContent>
        </Card>

        {/* Human approval */}
        <Card>
          <CardContent className="p-4 space-y-2" data-testid="detail-human-approval">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4" style={{ color: "hsl(40 48% 32%)" }} />
              <div className="font-display text-base">Human approval</div>
              {track.human_review_required ? (
                <span className="ml-auto text-[11px] px-1.5 py-0.5 rounded status-incomplete">Pending human review</span>
              ) : (
                <span className="ml-auto text-[11px] px-1.5 py-0.5 rounded status-client">Approved</span>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              Elevation to Internally-Ready / Client-Ready / Corporate-Ready requires explicit human approval. The system never auto-elevates.
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div><span className="text-muted-foreground">Approved by:</span> <span className="font-mono" data-testid="detail-approved-by">{track.human_approved_by || "—"}</span></div>
              <div><span className="text-muted-foreground">Approval date:</span> <span className="font-mono" data-testid="detail-approval-date">{(track.human_approval_date || "").slice(0, 19).replace("T", " ") || "—"}</span></div>
            </div>
          </CardContent>
        </Card>

        {/* Internal reviewer notes + actions */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div>
              <Label className="text-xs">Internal reviewer notes</Label>
              <Textarea
                value={reviewerNotes}
                onChange={(e) => setReviewerNotes(e.target.value)}
                rows={3}
                placeholder="Notes attached to the next clearance/attorney-review action"
                data-testid="detail-reviewer-notes"
              />
            </div>

            {canAct ? (
              <>
                <Label className="text-xs">Set clearance status</Label>
                <Select onValueChange={(v) => setClearance(v)} disabled={savingClearance}>
                  <SelectTrigger data-testid="detail-clearance-select"><SelectValue placeholder="Choose target status…" /></SelectTrigger>
                  <SelectContent>
                    {CLEARANCE_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>

                <div className="flex gap-2 pt-1 flex-wrap">
                  {!track.attorney_review_required ? (
                    <Button variant="outline" onClick={() => toggleAttorneyReview(true)} data-testid="detail-flag-attorney" style={{ borderColor: "hsl(0 70% 42%)", color: "hsl(0 70% 42%)" }}>
                      <Gavel className="w-4 h-4 mr-2" />Flag Attorney Review
                    </Button>
                  ) : (
                    <Button variant="outline" onClick={() => toggleAttorneyReview(false)} data-testid="detail-clear-attorney">
                      Clear Attorney Review flag
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={() => window.location.assign(`/ai-review-notes?generate=${track.id}`)}
                    data-testid="detail-generate-ai-note"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />Generate AI Review Note
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-xs text-muted-foreground">Your role cannot change clearance status. Contact a GFMP reviewer or super_admin.</div>
            )}
          </CardContent>
        </Card>
      </div>

      <SheetFooter className="mt-4">
        <div className="text-[11px] text-muted-foreground">
          Compliance support only · Not legal advice · Final clearance may require qualified music/IP counsel.
        </div>
      </SheetFooter>
    </>
  );
}

function SectionList({ icon: Icon, title, items, tone, testid }) {
  const list = items || [];
  return (
    <div className="flex items-start gap-3" data-testid={testid}>
      <Icon className="w-4 h-4 mt-1" style={{ color: tone }} />
      <div className="flex-1">
        <div className="text-sm font-medium">{title}</div>
        {list.length === 0 ? (
          <div className="text-sm text-muted-foreground">None</div>
        ) : (
          <ul className="text-sm text-muted-foreground list-disc list-inside">
            {list.map((x, i) => <li key={i}>{x}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
}
